// Language utilities for filtering and sorting movies/shows
const LANGUAGE_MAPPINGS = {
  // English variations
  'EN': { name: 'English', flag: '🇺🇸' },
  'English': { name: 'English', flag: '🇺🇸' },
  
  // Hindi variations
  'HI': { name: 'Hindi', flag: '🇮🇳' },
  'Hindi': { name: 'Hindi', flag: '🇮🇳' },
  
  // Indian regional languages
  'Telugu': { name: 'Telugu', flag: '🇮🇳' },
  'Tamil': { name: 'Tamil', flag: '🇮🇳' },
  'Malayalam': { name: 'Malayalam', flag: '🇮🇳' },
  'Kannada': { name: 'Kannada', flag: '🇮🇳' },
  'Bengali': { name: 'Bengali', flag: '🇮🇳' },
  'Marathi': { name: 'Marathi', flag: '🇮🇳' },
  'Gujarati': { name: 'Gujarati', flag: '🇮🇳' },
  'Punjabi': { name: 'Punjabi', flag: '🇮🇳' },
  'Assamese': { name: 'Assamese', flag: '🇮🇳' },
  'Odia': { name: 'Odia', flag: '🇮🇳' },
  'Urdu': { name: 'Urdu', flag: '🇮🇳' },
  
  // International languages
  'Spanish': { name: 'Spanish', flag: '🇪🇸' },
  'French': { name: 'French', flag: '🇫🇷' },
  
  'Italian': { name: 'Italian', flag: '🇮🇹' },
  'Japanese': { name: 'Japanese', flag: '🇯🇵' },
  'Korean': { name: 'Korean', flag: '🇰🇷' },
  'Chinese': { name: 'Chinese', flag: '🇨🇳' },
  // 'German': { name: 'German', flag: '🇩🇪' },
  // 'Arabic': { name: 'Arabic', flag: '🇸🇦' },
  // 'Russian': { name: 'Russian', flag: '🇷🇺' },
  // 'Portuguese': { name: 'Portuguese', flag: '🇵🇹' },
  // 'Dutch': { name: 'Dutch', flag: '🇳🇱' },
  // 'Swedish': { name: 'Swedish', flag: '🇸🇪' },
  // 'Norwegian': { name: 'Norwegian', flag: '🇳🇴' },
  // 'Danish': { name: 'Danish', flag: '🇩🇰' },
  // 'Finnish': { name: 'Finnish', flag: '🇫🇮' },
  // 'Polish': { name: 'Polish', flag: '🇵🇱' },
  // 'Czech': { name: 'Czech', flag: '🇨🇿' },
  // 'Hungarian': { name: 'Hungarian', flag: '🇭🇺' },
  // 'Romanian': { name: 'Romanian', flag: '🇷🇴' },
  // 'Greek': { name: 'Greek', flag: '🇬🇷' },
  // 'Turkish': { name: 'Turkish', flag: '🇹🇷' },
  // 'Hebrew': { name: 'Hebrew', flag: '🇮🇱' },
  // 'Thai': { name: 'Thai', flag: '🇹🇭' },
  // 'Vietnamese': { name: 'Vietnamese', flag: '🇻🇳' },
  // 'Indonesian': { name: 'Indonesian', flag: '🇮🇩' },
  // 'Malay': { name: 'Malay', flag: '🇲🇾' },
  // 'Tagalog': { name: 'Tagalog', flag: '🇵🇭' },
  // 'Swahili': { name: 'Swahili', flag: '🇰🇪' }
};

export const getAvailableLanguages = (items = []) => {
  const languages = new Set();
  items.forEach(item => {
    if (item.language) {
      const normalized = normalizeLanguage(item.language);
      if (normalized) languages.add(normalized);
    }
  });
  
  return Array.from(languages).map(lang => ({
    code: lang,
    name: LANGUAGE_MAPPINGS[lang]?.name || lang,
    flag: LANGUAGE_MAPPINGS[lang]?.flag || '🌐'
  })).sort((a, b) => a.name.localeCompare(b.name));
};

// Create deduplicated list of supported languages (avoid showing Hindi/HI, English/EN duplicates)
const createSupportedLanguages = () => {
  const uniqueLanguages = new Map();
  
  Object.entries(LANGUAGE_MAPPINGS).forEach(([code, info]) => {
    const key = info.name;
    if (!uniqueLanguages.has(key)) {
      uniqueLanguages.set(key, { code, name: info.name, flag: info.flag });
    } else {
      // If we already have this language, prefer full names over codes
      const existing = uniqueLanguages.get(key);
      if (code.length > existing.code.length) {
        uniqueLanguages.set(key, { code, name: info.name, flag: info.flag });
      }
    }
  });
  
  return Array.from(uniqueLanguages.values()).sort((a, b) => a.name.localeCompare(b.name));
};

export const SUPPORTED_LANGUAGES = createSupportedLanguages();

export const getLanguageByCode = (code) => {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === code) || { code, name: code, flag: '🌐' };
};

// Normalize language preferences array (handle DynamoDB format)
export const normalizeLanguagePreferences = (preferences = []) => {
  if (!Array.isArray(preferences)) return [];
  return preferences.map(pref => 
    typeof pref === 'string' ? pref : pref?.S || pref
  ).filter(Boolean);
};

export const sortByLanguagePreference = (items, userLanguagePreferences = []) => {
  const normalizedPrefs = normalizeLanguagePreferences(userLanguagePreferences);
  if (!normalizedPrefs.length) return items;
  
  return [...items].sort((a, b) => {
    const aNormalizedLang = normalizeLanguage(a.language);
    const bNormalizedLang = normalizeLanguage(b.language);
    
    // Find preference index using normalized languages
    const aLangIndex = normalizedPrefs.findIndex(pref => normalizeLanguage(pref) === aNormalizedLang);
    const bLangIndex = normalizedPrefs.findIndex(pref => normalizeLanguage(pref) === bNormalizedLang);
    
    // Both languages are in preferences - sort by preference order (lower index = higher priority)
    if (aLangIndex !== -1 && bLangIndex !== -1) {
      return aLangIndex - bLangIndex;
    }
    
    // Only one language is in preferences
    if (aLangIndex !== -1 && bLangIndex === -1) return -1;
    if (aLangIndex === -1 && bLangIndex !== -1) return 1;
    
    // Neither language is in preferences - sort by popularity/rating
    return (b.popularity || 0) - (a.popularity || 0);
  });
};

// Language normalization mapping
const LANGUAGE_VARIANTS = {
  'Hindi': ['Hindi', 'HI', 'hi', 'hindi'],
  'English': ['English', 'EN', 'en', 'english'],
  'Telugu': ['Telugu', 'TE', 'te', 'telugu'],
  'Tamil': ['Tamil', 'TA', 'ta', 'tamil'],
  'Malayalam': ['Malayalam', 'ML', 'ml', 'malayalam'],
  'Kannada': ['Kannada', 'KN', 'kn', 'kannada'],
  'Bengali': ['Bengali', 'BN', 'bn', 'bengali'],
  'Marathi': ['Marathi', 'MR', 'mr', 'marathi'],
  'Gujarati': ['Gujarati', 'GU', 'gu', 'gujarati'],
  'Punjabi': ['Punjabi', 'PA', 'pa', 'punjabi']
};

// Normalize language to standard form
export const normalizeLanguage = (language) => {
  if (!language) return null;
  
  for (const [standard, variants] of Object.entries(LANGUAGE_VARIANTS)) {
    if (variants.includes(language)) {
      return standard;
    }
  }
  return language;
};



export const filterByLanguages = (items, selectedLanguages = []) => {
  if (!selectedLanguages.length) return items;
  
  return items.filter(item => {
    if (!item.language) return false;
    
    // Handle comma-separated languages
    const itemLanguages = item.language.split(',').map(lang => normalizeLanguage(lang.trim()));
    
    return selectedLanguages.some(selectedLang => {
      const normalizedSelectedLang = normalizeLanguage(selectedLang);
      return itemLanguages.includes(normalizedSelectedLang);
    });
  });
};

// Filter items by genres
export const filterByGenres = (items, selectedGenres = []) => {
  if (!selectedGenres.length) return items;
  
  return items.filter(item => {
    if (!item.genre) return false;
    
    // Handle both string and array genre formats
    const itemGenres = Array.isArray(item.genre) 
      ? item.genre 
      : item.genre.split(',').map(g => g.trim());
    
    return selectedGenres.some(selectedGenre => 
      itemGenres.some(itemGenre => 
        itemGenre.toLowerCase().includes(selectedGenre.toLowerCase())
      )
    );
  });
};

// Combined filtering function
export const filterContent = (items, filters = {}) => {
  const { languages = [], genres = [] } = filters;
  let filteredItems = items;
  
  // Apply language filter
  if (languages.length > 0) {
    filteredItems = filterByLanguages(filteredItems, languages);
  }
  
  // Apply genre filter
  if (genres.length > 0) {
    filteredItems = filterByGenres(filteredItems, genres);
  }
  
  return filteredItems;
};